import React from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import z from "zod";

import Button from "@/components/button";

import { QueryKeys } from "@/queries/queryKeys";
import type { Profile } from "@/schemas/profile";
import { useRole } from "@/utils/hooks/use-role";
import type { Role } from "@/utils/hooks/use-role";

import { ListView } from "./-components/list-view";
import { ShiftDetails } from "./-components/shift-details";
import { UserDetails } from "./-components/user-details";
import { WorkerListView } from "./-components/worker-list-view";
import { performSearch } from "./-helpers";

import type { Shift } from "./-components/shift-details";

const SearchParamsSchema = z.object({
  role_name: z.string().optional(),
  when: z.enum(["today", "weekend"]).optional(),
  lat: z.union([z.string(), z.number()]).optional(),
  lng: z.union([z.string(), z.number()]).optional(),
  radiusMiles: z.union([z.string(), z.number()]).optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  shiftDays: z.string().optional(),
});

type SearchParams = z.infer<typeof SearchParamsSchema>;

export const Route = createFileRoute("/app/$role/_app/search/list")({
  validateSearch: SearchParamsSchema,
  loaderDeps: ({ search: { role_name, when, lat, lng, radiusMiles } }) => ({
    role_name,
    when,
    lat,
    lng,
    radiusMiles,
  }),

  loader: async ({ params, location }) => {
    const search: SearchParams = location.search;
    const role = params.role as Role;
    return {
      queryKey: [QueryKeys.SEARCH, role, search],
      queryFn: async () => performSearch({ role, searchParams: search }),
    };
  },

  component: ListPage,
  staticData: {
    layout: {
      boxed: false,
    },
  },
});

function ListPage() {
  const { t } = useTranslation("search");
  const role = useRole();
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const { data: searchData } = useSuspenseQuery({
    queryKey: [QueryKeys.SEARCH, role, searchParams],
    queryFn: async () => performSearch({ role, searchParams }),
  });

  const shifts: Array<Shift> = searchData.shifts;
  const users: Array<Profile> = searchData.users;

  const [selected, setSelected] = React.useState<Shift | Profile | null>(null);

  return (
    <>
      <div className="fixed bottom-[70px] left-1/2 z-30 -translate-x-1/2 rounded-3xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.15)]">
        <Button
          type="button"
          onClick={() =>
            navigate({
              to: "/app/$role/search",
              params: { role },
              search: searchParams,
            })
          }
          className="rounded-3xl px-5 h-9 text-sm font-normal bg-garnet text-white hover:bg-garnet"
        >
          {t("showMap")}
        </Button>
      </div>
      {role === "worker" ? (
        <ListView shifts={shifts} onSelect={(s) => setSelected(s)} />
      ) : users.length > 0 ? (
        <WorkerListView users={users} onSelect={(w) => setSelected(w)} />
      ) : (
        <div className="p-6 text-center text-sm text-gray-700">
          {t("noUsersFound")}
        </div>
      )}
      {selected && "id" in selected ? (
        <ShiftDetails
          shift={selected as Shift}
          role={role}
          onClose={() => setSelected(null)}
          onInterested={(id) => {
            console.debug("Interested click", { id });
          }}
          onMessage={(id) => {
            const shift = shifts.find((s) => s.id === String(id));
            if (shift && shift.business && shift.business.user_id) {
              navigate({
                to: "/app/$role/messages",
                params: { role },
                search: {
                  tab: "messages",
                  to: shift.business.user_id,
                  businessId: shift.business.id,
                  selectedOfferId: undefined,
                },
              });
            }
          }}
        />
      ) : selected ? (
        <UserDetails
          open={true}
          user={selected}
          onClose={() => setSelected(null)}
          onSendOffer={(userId) => {
            console.debug("Send Offer", { userId });
          }}
          onSendMessage={(userId) => {
            console.debug("Send Message", { userId });
          }}
        />
      ) : null}
    </>
  );
}
